import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * The separator the backend uses to join a base shipping-option id with a live
 * Shopify rate handle into one composite picker id. Mirrors
 * `buildCompositeShippingOptionId` / `parseCompositeShippingOptionId` in the api
 * repo (`packages/modules/b2c-core/src/workflows/cart/utils/live-shipping-option-id.ts`).
 * It must stay in lockstep with that constant: a divergence silently unmatches
 * every live rate against the picker list, which is exactly the KAN-108 break.
 */
const COMPOSITE_SHIPPING_OPTION_SEPARATOR = "::";

type ShippingMethod =
  SalonStockerHttpTypes.StoreSellerCart["shipping_methods"][number];

/**
 * Join a base shipping-option id and a live rate handle into the composite id
 * the picker list identifies that rate by - the storefront half of the
 * backend's `buildCompositeShippingOptionId`. Internal: the only consumer is
 * {@link getSelectedShippingPickerId}. There is deliberately no `parse`
 * counterpart here - the storefront posts a composite id whole and never
 * decomposes one; the backend owns the split.
 */
function buildCompositeShippingOptionId(
  baseOptionId: string,
  rateHandle: string
): string {
  return `${baseOptionId}${COMPOSITE_SHIPPING_OPTION_SEPARATOR}${rateHandle}`;
}

/**
 * The id the storefront shipping picker identifies the *selected* method by -
 * the same identity `GET /store/shipping-options` uses in its list, which for a
 * live Shopify rate is the composite `base::handle`, not the bare base id core
 * persists on the method.
 *
 * On add-to-cart the route splits a composite option id: it hands core the BASE
 * `shipping_option_id` (so core's option lookup resolves) and persists the
 * discriminating rate handle separately, which the seller-carts response
 * surfaces as the typed `live_rate_handle` field (KAN-115). Reading
 * `shipping_option_id` alone therefore yields the base, which never equals the
 * composite ids in the picker list - so the selection reads invalid, the
 * selector falls to its "Select shipping method" placeholder with a red error
 * border, and an auto-select effect then silently reverts a non-default pick
 * (Express -> Standard, a money-changing write) on the next mount (KAN-108).
 * Re-joining the base with `live_rate_handle` reconstructs the picker's own id
 * and restores the match.
 *
 * This deliberately reconstructs the exact handle rather than matching on
 * amount: amount-matching is ambiguous for equal-priced rates and re-breaks on
 * any live reprice that shifts `amount` between the persisted method and the
 * freshly-quoted option list.
 *
 * A plain (non-integration) method carries no handle and returns its
 * `shipping_option_id` verbatim. The in-flight optimistic record already holds
 * the composite id it was handed (with no `live_rate_handle`), so it too passes
 * through unchanged. Returns `undefined` when there is no selected method,
 * matching the `shipping_methods[0]?.shipping_option_id` reads it replaces.
 */
export function getSelectedShippingPickerId(
  method: ShippingMethod | undefined | null
): string | undefined {
  const baseOptionId = method?.shipping_option_id;
  if (!baseOptionId) return undefined;

  const handle = method?.live_rate_handle;

  return handle
    ? buildCompositeShippingOptionId(baseOptionId, handle)
    : baseOptionId;
}

/** The fields of a shipping option the picker reads. */
export interface PickerShippingOption {
  id: string;
  name: string;
  amount?: number | null;
}

/**
 * A cart's persisted shipping method lined up against the option list the
 * picker shows. See {@link reconcileShippingSelection}.
 */
export interface ReconciledShippingSelection<T extends PickerShippingOption> {
  /** The list the picker renders. */
  options: T[];
  /**
   * The id in `options` the persisted method is shown as, or `undefined` when
   * the cart has no method or the list offers nothing that method matches.
   */
  selectedOptionId: string | undefined;
  /**
   * Whether the cart carries a method the list no longer offers - the state
   * the server's own reconcile (api `refresh-seller-cart-shipping-methods.ts`,
   * KAN-121) deletes on the next cart refresh, and auto-select must replace.
   */
  isStale: boolean;
}

/**
 * Lines the cart's persisted shipping method up against the option list, by
 * the same rule the server keeps or deletes that method with
 * (`findOptionForShippingMethod` in the api's
 * `refresh-seller-cart-shipping-methods.ts`): the method matches an option by
 * its composite picker id (a live rate, see
 * {@link getSelectedShippingPickerId}) or by its bare base option id.
 *
 * The base-id match is the case this exists for (KAN-230). Once a payment
 * session price-locks the cart (KAN-85), or when a live quote fails open, the
 * list comes back as the seller's persisted baseline row - bare id, estimate
 * price - while the cart still holds the live rate it was quoted. The server
 * keeps that method; reading only the composite id, the picker called it
 * invalid and auto-select POSTed the baseline over it, re-pricing a $11 live
 * rate to the $9.95 estimate on a plain reload. Here that match stays selected,
 * and the baseline row is shown as the method the cart actually holds - its
 * name and amount - so the picker never displays a price the cart does not
 * charge.
 */
export function reconcileShippingSelection<T extends PickerShippingOption>(
  options: T[],
  method: ShippingMethod | undefined | null
): ReconciledShippingSelection<T> {
  const pickerId = getSelectedShippingPickerId(method);
  if (!method || !pickerId) {
    return { options, selectedOptionId: undefined, isStale: false };
  }

  if (options.some((option) => option.id === pickerId)) {
    return { options, selectedOptionId: pickerId, isStale: false };
  }

  const baseOptionId = method.shipping_option_id;
  const baseMatch = options.find((option) => option.id === baseOptionId);
  if (!baseMatch) {
    return { options, selectedOptionId: undefined, isStale: true };
  }

  return {
    options: options.map((option) =>
      option === baseMatch
        ? { ...option, name: method.name, amount: method.amount }
        : option
    ),
    selectedOptionId: baseMatch.id,
    isStale: false
  };
}

/**
 * The single rule for "is there a usable shipping selection right now" -
 * `isSelectionValid` from `useShippingMethodSync` alone isn't enough, since
 * it reads vacuously true when nothing is selected yet (it only flags a stale
 * method - see {@link reconcileShippingSelection}). Shared by `ShippingSelector`
 * (falls back to the placeholder when this is `undefined`) and checkout's
 * `SellerShippingRow` (flags its required-field error when this is
 * `undefined`) - both callers must treat "invalid" and "nothing chosen yet"
 * as the same case, and this is where that agreement lives.
 */
export function getEffectiveShippingSelection(
  isSelectionValid: boolean | undefined,
  selectedOptionId: string | null | undefined
): string | undefined {
  return isSelectionValid && selectedOptionId ? selectedOptionId : undefined;
}
